/** Shared inspection log cell helpers (ITP + PQT). */

export const REJECT_CELL_COLOR = '#cf1322';
export const ACCEPT_CELL_COLOR = '#10b981';

export function coerceToJsonString(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function parseJsonCellValue(value) {
  const str = coerceToJsonString(value);
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch {
    try {
      return JSON.parse(str.replace(/\\"/g, '"'));
    } catch {
      return null;
    }
  }
}

export function isValidJson(str) {
  return parseJsonCellValue(str) != null;
}

export function isTableJsonValue(value) {
  const parsed = parseJsonCellValue(value);
  return Boolean(parsed && Array.isArray(parsed.rows));
}

export function formatTableCellWithUnit(rawValue, rowUnit, fallbackUnit) {
  if (rawValue === '' || rawValue == null) return rawValue;
  const rowU = String(rowUnit ?? '').trim();
  const fallbackU = String(fallbackUnit ?? '').trim();
  const isTypeLabel = (u) => /^table$/i.test(u);
  const u = rowU || (isTypeLabel(fallbackU) ? '' : fallbackU);
  if (!u) return String(rawValue);
  const body = String(rawValue).trim();
  if (body.toLowerCase().endsWith(u.toLowerCase())) return body;
  return `${body} ${u}`.trim();
}

export function isValueInRangeWithParams(value, comparator, minValue, maxValue) {
  const n = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : NaN);
  const minV = n(minValue);
  const maxV = n(maxValue);

  switch (comparator) {
    case '<':
      return !Number.isNaN(maxV) && value < maxV;
    case '<=':
      return !Number.isNaN(maxV) && value <= maxV;
    case '>':
      return !Number.isNaN(minV) && value > minV;
    case '>=':
      return !Number.isNaN(minV) && value >= minV;
    case '=':
      return !Number.isNaN(maxV) && value === maxV;
    case 'BETWEEN':
      return !Number.isNaN(minV) && !Number.isNaN(maxV) && value >= minV && value <= maxV;
    default:
      return false;
  }
}

export function parseTestTableData(testItem) {
  let table_data = testItem?.table_data ?? testItem?.tableData;
  if (typeof table_data === 'string') {
    try {
      table_data = JSON.parse(table_data);
    } catch {
      table_data = null;
    }
  }
  return table_data;
}

/** Single (per table): one unit/comparator for the whole table — no cell-level checks. */
export function isTableSingleComparatorMode(testItem) {
  const table_data = parseTestTableData(testItem);
  if (table_data?.mode === 'single') return true;
  if (table_data?.mode === 'multiple') return false;
  if (testItem?.mode === 'single') return true;
  if (testItem?.mode === 'multiple') return false;
  const comp = testItem?.comparator;
  return comp != null && String(comp).trim() !== '' && comp !== 'None';
}

/** Multiple (per row): each row has its own unit/comparator — row-level checks only. */
export function isTableMultipleComparatorMode(testItem) {
  const table_data = parseTestTableData(testItem);
  if (table_data?.mode === 'multiple') return true;
  if (table_data?.mode === 'single') return false;
  if (testItem?.mode === 'multiple') return true;
  if (testItem?.mode === 'single') return false;
  const comp = testItem?.comparator;
  return comp == null || String(comp).trim() === '';
}

function resolveTableRowComparatorParams(valueRow, templateRow, testItem) {
  if (isTableSingleComparatorMode(testItem)) {
    return {
      rowMaxValue: parseFloat(testItem?.max_value ?? testItem?.maxValue),
      rowMinValue: parseFloat(testItem?.min_value ?? testItem?.minValue),
      rowComparator: testItem?.comparator,
    };
  }

  let { maxValue, minValue, comparator } = valueRow || {};

  const t = templateRow;
  if (t?.maxValue !== undefined && t.maxValue !== null && t.maxValue !== '') {
    maxValue = t.maxValue;
  } else if (t?.max_value !== undefined && t.max_value !== null && t.max_value !== '') {
    maxValue = t.max_value;
  }
  if (t?.minValue !== undefined && t.minValue !== null && t.minValue !== '') {
    minValue = t.minValue;
  } else if (t?.min_value !== undefined && t.min_value !== null && t.min_value !== '') {
    minValue = t.min_value;
  }
  if (t?.comparator !== undefined && t.comparator !== null && t.comparator !== '') {
    comparator = t.comparator;
  }

  const rowMaxValue =
    maxValue != null && maxValue !== ''
      ? parseFloat(maxValue)
      : parseFloat(testItem?.max_value ?? testItem?.maxValue);
  const rowMinValue =
    minValue != null && minValue !== ''
      ? parseFloat(minValue)
      : parseFloat(testItem?.min_value ?? testItem?.minValue);
  const rowComparator = comparator || testItem?.comparator;

  return { rowMaxValue, rowMinValue, rowComparator };
}

function evaluateNumericCellRange(cellValue, valueRow, templateRow, testItem) {
  const { rowMaxValue, rowMinValue, rowComparator } = resolveTableRowComparatorParams(
    valueRow,
    templateRow,
    testItem
  );

  if (rowComparator === 'None' || !rowComparator) {
    return null;
  }

  if (cellValue === '' || cellValue == null) {
    return false;
  }

  const numericValue = parseFloat(cellValue);
  if (Number.isNaN(numericValue)) {
    return false;
  }

  return isValueInRangeWithParams(numericValue, rowComparator, rowMinValue, rowMaxValue);
}

/** @returns {true | false | null} — null when no comparator is configured or in single-table mode */
export function getTableCellRangeStatus(cellValue, valueRow, templateRow, testItem) {
  if (isTableSingleComparatorMode(testItem) || isTableMultipleComparatorMode(testItem)) {
    return null;
  }

  return evaluateNumericCellRange(cellValue, valueRow, templateRow, testItem);
}

/** Multiple (per row) mode only — row-level pass/fail for modal icons. */
export function getTableRowRangeStatus(valueRow, templateRow, testItem) {
  if (!isTableMultipleComparatorMode(testItem)) {
    return null;
  }

  const { rowComparator } = resolveTableRowComparatorParams(valueRow, templateRow, testItem);
  if (rowComparator === 'None' || !rowComparator) {
    return null;
  }

  const cells = valueRow?.values || [];
  if (cells.length === 0) {
    return false;
  }

  for (const cell of cells) {
    const cellStatus = evaluateNumericCellRange(cell, valueRow, templateRow, testItem);
    if (cellStatus === false) {
      return false;
    }
  }

  return true;
}

function validateTableRow(row, test, tableDataRow) {
  if (isTableSingleComparatorMode(test)) {
    return true;
  }

  const rowStatus = getTableRowRangeStatus(row, tableDataRow, test);
  if (rowStatus === null) {
    return true;
  }

  return rowStatus !== false;
}

export function validateTableTest(testValue, weldTest) {
  const tableData = parseJsonCellValue(testValue);
  if (!tableData?.rows || !Array.isArray(tableData.rows)) {
    return 'reject';
  }

  if (isTableSingleComparatorMode(weldTest)) {
    return 'accept';
  }

  try {
    const templateRows = parseTestTableData(weldTest)?.rows;
    for (let i = 0; i < tableData.rows.length; i++) {
      const row = tableData.rows[i];
      const tableDataRow = templateRows?.[i];
      if (!validateTableRow(row, weldTest, tableDataRow)) {
        return 'reject';
      }
    }
    return 'accept';
  } catch {
    return 'reject';
  }
}

export function validateNumericValue(testValue, test) {
  if (!isValidJson(testValue)) {
    return 'reject';
  }
  try {
    const parsedValue = JSON.parse(testValue);
    const comparator = test.comparator;
    const minValue =
      test.min_value != null && test.min_value !== '' ? parseFloat(test.min_value) : NaN;
    const maxValue =
      test.max_value != null && test.max_value !== '' ? parseFloat(test.max_value) : NaN;

    if (Array.isArray(parsedValue)) {
      if (parsedValue.includes(null)) {
        return 'reject';
      }
      for (const v of parsedValue) {
        const num = typeof v === 'number' ? v : parseFloat(v);
        if (Number.isNaN(num)) {
          return 'reject';
        }
        if (!isValueInRangeWithParams(num, comparator, minValue, maxValue)) {
          return 'reject';
        }
      }
      return 'accept';
    }

    const num = typeof parsedValue === 'number' ? parsedValue : parseFloat(parsedValue);
    if (Number.isNaN(num)) {
      return 'reject';
    }
    return isValueInRangeWithParams(num, comparator, minValue, maxValue) ? 'accept' : 'reject';
  } catch {
    return 'reject';
  }
}

export function resolveTestCriteriaType(testDef) {
  if (!testDef) return null;
  return testDef.testType ?? testDef.testTypeOfTest ?? null;
}

export function buildSyntheticTestItem(cellValue, testDef, testTypesList) {
  const typeRow = testTypesList?.find((t) => t.id === testDef?.testValueTypeId);
  const valueType = typeRow?.valueType ?? testDef?.testValueType;
  const displayUnit =
    typeRow?.displayStr ?? typeRow?.display_str ?? typeRow?.value ?? '';

  let table_data = parseTestTableData(testDef);

  return {
    type_of_test: resolveTestCriteriaType(testDef),
    value: cellValue,
    comparator: testDef?.comparator,
    min_value: testDef?.minValue ?? testDef?.min_value,
    max_value: testDef?.maxValue ?? testDef?.max_value,
    mandatory: testDef?.mandatory,
    mode: testDef?.mode ?? table_data?.mode,
    table_data,
    not_applicable: cellValue === 'N/A' ? 'yes' : undefined,
    test_value_type: {
      value_type: valueType,
      display_str: displayUnit,
    },
  };
}

/** Mirrors UpdatePqtWeldService.calculateTestStatus for grid display. */
export function calculateInspectionTestStatus(item) {
  const valueType = item.test_value_type?.value_type;
  const typeOfTest = item.type_of_test;
  const testValue = item.value;
  const mandatory = item.mandatory;
  const notApplicable = item.not_applicable;
  const comparator = item.comparator;

  if (notApplicable === 'yes') {
    return 'accept';
  }
  if (typeOfTest === 'Text') {
    return 'accept';
  }
  if (testValue === 'update_pending') {
    return 'pending';
  }

  if (typeOfTest === 'Table' || isTableJsonValue(testValue)) {
    return validateTableTest(testValue, item);
  }

  if (typeOfTest === 'Value') {
    if (testValue === 'accept') {
      return 'accept';
    }

    if (valueType === 'ACCEPT_OR_REJECT_OR_NA') {
      if (testValue === 'N/A') {
        return 'accept';
      }
      if (testValue === 'reject') {
        return 'reject';
      }
      return 'reject';
    }

    if (valueType !== 'ACCEPT_OR_REJECT_OR_NA') {
      if ((testValue === '' || testValue == null) && !mandatory) {
        return 'accept';
      }
      if (comparator === 'None') {
        return 'accept';
      }
      const hasComparator =
        comparator != null && String(comparator).trim() !== '' && comparator !== 'None';
      if (hasComparator) {
        return validateNumericValue(String(testValue ?? ''), item);
      }
    }

    return 'reject';
  }

  if (typeOfTest === 'Accept/Reject/NA' || valueType === 'ACCEPT_OR_REJECT_OR_NA') {
    if (testValue === 'accept' || testValue === 'N/A') {
      return 'accept';
    }
    if (testValue === 'reject') {
      return 'reject';
    }
    return 'reject';
  }

  return 'reject';
}

export function extractValueParts(cellValue) {
  if (cellValue == null || String(cellValue).trim() === '') return [];

  const raw = String(cellValue).trim();
  const parseAsJsonArray = (str) => {
    const t = str.trim();
    if (!(t.startsWith('[') && t.endsWith(']'))) return null;
    try {
      const p = JSON.parse(t);
      return Array.isArray(p) ? p : null;
    } catch {
      try {
        const p = JSON.parse(t.replace(/\\"/g, '"'));
        return Array.isArray(p) ? p : null;
      } catch {
        return null;
      }
    }
  };

  const cleanSegment = (s) =>
    String(s)
      .trim()
      .replace(/\\"/g, '"')
      .replace(/^["']+|["']+$/g, '')
      .replace(/^\[|\]$/g, '')
      .trim();

  const parsed = parseAsJsonArray(raw);
  if (parsed) {
    return parsed.map((v) => (v == null ? '' : String(v).trim())).filter((p) => p !== '');
  }

  return raw
    .replace(/^\[|\]$/g, '')
    .split(',')
    .map((s) => cleanSegment(s))
    .filter((p) => p !== '');
}

export function formatValueDisplayString(cellValue, unit) {
  if (cellValue == null || String(cellValue).trim() === '') return '-';

  const parts = extractValueParts(cellValue);
  if (parts.length === 0) return '-';

  const body = parts.join(', ').replace(/\[|\]/g, '').trim();
  const unitStr = String(unit ?? '').trim();
  const suffix = unitStr && !/^table$/i.test(unitStr) ? ` ${unitStr}` : '';
  return `${body}${suffix}`.trim();
}

export function getColumnTitleText(column) {
  if (!column) return '';
  if (typeof column.columnTitle === 'string') return column.columnTitle;
  if (typeof column.title === 'string') return column.title;

  const extractFromNode = (node) => {
    if (node == null) return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) {
      for (const child of node) {
        const text = extractFromNode(child);
        if (text) return text;
      }
      return '';
    }
    if (node.props) {
      if (typeof node.props.title === 'string') return node.props.title;
      return extractFromNode(node.props.children);
    }
    return '';
  };

  return extractFromNode(column.title) || String(column.dataIndex ?? '');
}

function looseTestKey(str) {
  return String(str ?? '')
    .toLowerCase()
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[^a-z0-9]/g, '');
}

export function slugifyTestName(name) {
  return String(name ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

export function findTestDefinitionForColumn(column, stationTests) {
  if (!column || !Array.isArray(stationTests) || stationTests.length === 0) {
    return null;
  }
  const dataIndex = column.dataIndex || column.key;
  const title = getColumnTitleText(column);
  const dataKey = looseTestKey(dataIndex);
  const titleKey = looseTestKey(title);

  const byDataIndex = stationTests.find(
    (t) =>
      slugifyTestName(t.typeOfTest) === dataIndex ||
      looseTestKey(t.typeOfTest) === dataKey
  );
  if (byDataIndex) return byDataIndex;

  if (title) {
    const byTitle = stationTests.find(
      (t) =>
        String(t.typeOfTest ?? '').toLowerCase() === title.toLowerCase() ||
        looseTestKey(t.typeOfTest) === titleKey
    );
    if (byTitle) return byTitle;
  }

  return null;
}

export function getTableColumnsForModal(item) {
  if (!item?.value) return [];

  const rawUnit = item.test_value_type?.display_str || '';
  const unit = /^table$/i.test(String(rawUnit).trim()) ? '' : rawUnit;
  const columns = [
    {
      title: unit || ' ',
      dataIndex: 'name',
      key: 'name',
    },
  ];

  try {
    const parsedData = parseTestTableData(item);
    if (!parsedData?.columns || !Array.isArray(parsedData.columns)) return columns;

    parsedData.columns.forEach((col, index) => {
      columns.push({
        title: col,
        dataIndex: `col_${index}`,
        key: `col_${index}`,
        align: 'center',
      });
    });
  } catch {
    return columns;
  }

  return columns;
}

export function getTableDataForModal(item) {
  if (!item?.value) return [];

  try {
    const parsedData = parseJsonCellValue(item.value);
    const templateRows = parseTestTableData(item);
    if (!parsedData?.rows || !Array.isArray(parsedData.rows)) return [];
    if (!templateRows?.rows || !Array.isArray(templateRows.rows)) return [];

    const fallbackUnit = item.test_value_type?.display_str || '';

    return templateRows.rows.map((row, rowIndex) => {
      const valueRow = parsedData.rows[rowIndex];
      const cellValues = Array.isArray(valueRow?.values) ? valueRow.values : [];
      const numCols = Math.max(
        cellValues.length,
        row.values?.length ?? 0,
        parsedData.columns?.length ?? 0,
        templateRows.columns?.length ?? 0
      );

      const rowData = { key: rowIndex, name: row.name };
      for (let index = 0; index < numCols; index++) {
        const value = cellValues[index] ?? '';
        rowData[`col_${index}`] = formatTableCellWithUnit(value, row.unit, fallbackUnit);
      }
      return rowData;
    });
  } catch {
    return [];
  }
}
